export function PricePerUnitMethod() {
  return (
    <section className="py-20 px-4 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Quick Valuation Method: Price Per Unit
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Price per unit is the simplest and fastest way to get a ballpark estimate of your apartment building's value. While not as precise as the three primary methods, it provides a quick reality check and helps you understand general market pricing.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <img
              src="https://public.readdy.ai/ai/img_res/eb46b3476fb1211927fedcc866e567d2.jpg"
              alt="Price per unit calculation for apartment buildings"
              className="w-full h-96 object-cover object-top rounded-lg shadow-lg"
            />
          </div>
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">What Is Price Per Unit?</h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Price per unit is calculated by dividing the total sale price of an apartment building by the number of rental units. This metric provides a simple way to compare properties of different sizes and quickly estimate value based on market averages.
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              The formula is straightforward: <strong>Price Per Unit = Total Sale Price ÷ Number of Units</strong>. For example, if a 20-unit building sells for $2,000,000, the price per unit is $100,000. You can then apply this metric to your property for a quick estimate.
            </p>
            <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded-r-lg">
              <h4 className="font-bold text-gray-900 mb-2 text-lg">Simple Formula:</h4>
              <p className="text-gray-800 text-xl font-semibold mb-2">Price Per Unit = Sale Price ÷ Units</p>
              <p className="text-gray-600">The quickest way to estimate apartment building value</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8 lg:p-12 mb-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">How to Use Price Per Unit</h3>
          
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-4">
                <span className="text-teal-600 font-bold text-2xl">1</span>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Research Comparable Sales</h4>
              <p className="text-gray-600 leading-relaxed">
                Find 3-5 recent sales of similar apartment buildings in your area and calculate their price per unit.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-4">
                <span className="text-teal-600 font-bold text-2xl">2</span>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Calculate Average</h4>
              <p className="text-gray-600 leading-relaxed">
                Determine the average price per unit from your comparable sales to establish a market baseline.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-4">
                <span className="text-teal-600 font-bold text-2xl">3</span>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Apply to Your Property</h4>
              <p className="text-gray-600 leading-relaxed">
                Multiply the average price per unit by your building's unit count for a quick value estimate.
              </p>
            </div>
          </div>

          <div className="max-w-4xl mx-auto bg-gradient-to-r from-gray-800 to-gray-900 rounded-lg p-8 text-white">
            <h4 className="text-2xl font-bold mb-6 text-center">Price Per Unit Example</h4>
            
            <div className="space-y-4 mb-6">
              <div className="bg-white/10 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span>Comp #1: 18 units sold for $1,890,000</span>
                  <span className="font-bold">$105,000/unit</span>
                </div>
              </div>
              <div className="bg-white/10 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span>Comp #2: 22 units sold for $2,200,000</span>
                  <span className="font-bold">$100,000/unit</span>
                </div>
              </div>
              <div className="bg-white/10 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span>Comp #3: 20 units sold for $2,100,000</span>
                  <span className="font-bold">$105,000/unit</span>
                </div>
              </div>
              <div className="bg-white/10 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span>Comp #4: 24 units sold for $2,520,000</span>
                  <span className="font-bold">$105,000/unit</span>
                </div>
              </div>
            </div>

            <div className="border-t border-white/30 pt-6">
              <p className="text-lg mb-2">Average Price Per Unit: <strong>$103,750</strong></p>
              <p className="text-lg mb-4">Your Property: <strong>20 units</strong></p>
              <p className="text-2xl font-bold">Estimated Value: 20 × $103,750 = <strong>$2,075,000</strong></p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Factors That Affect Price Per Unit</h3>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Price per unit varies significantly based on location, property quality, unit size, and market conditions. Understanding these factors helps you make appropriate adjustments when using this quick valuation method.
            </p>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0 mt-1">
                  <i className="ri-map-pin-line text-teal-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Location Quality</h4>
                  <p className="text-gray-600">Prime locations command $120K-$200K+ per unit, while secondary markets may be $50K-$100K per unit</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0 mt-1">
                  <i className="ri-home-smile-line text-teal-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Unit Size & Mix</h4>
                  <p className="text-gray-600">Larger units (2-3 bedrooms) typically have higher price per unit than studios and 1-bedrooms</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0 mt-1">
                  <i className="ri-star-line text-teal-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Property Class</h4>
                  <p className="text-gray-600">Class A properties command premium pricing, while Class C properties sell at significant discounts</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0 mt-1">
                  <i className="ri-tools-line text-teal-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Property Condition</h4>
                  <p className="text-gray-600">Well-maintained properties with recent updates sell for 20-40% more per unit than distressed properties</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0 mt-1">
                  <i className="ri-line-chart-line text-teal-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Market Conditions</h4>
                  <p className="text-gray-600">Strong seller's markets increase price per unit, while buyer's markets decrease it</p>
                </div>
              </div>
            </div>
          </div>
          <div>
            <img
              src="https://readdy.ai/api/search-image?query=apartment-building-comparison-different-property-classes-luxury-versus-standard-versus-value-properties-real-estate-market-tiers-property-quality-levels-multifamily-housing-comparison-investment-grades&width=800&height=600&seq=property-comparison-001&orientation=landscape"
              alt="Factors affecting price per unit in apartment valuations"
              className="w-full h-96 object-cover object-top rounded-lg shadow-lg"
            />
          </div>
        </div>

        <div className="mt-16 bg-yellow-50 border-l-4 border-yellow-500 p-8 rounded-r-lg">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 flex items-center justify-center bg-yellow-100 rounded-full flex-shrink-0">
              <i className="ri-alert-line text-yellow-600 text-2xl"></i>
            </div>
            <div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Important Limitations</h4>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                While price per unit is useful for quick estimates, it has significant limitations. It doesn't account for differences in unit sizes, income levels, operating expenses, property condition, or location quality. Two 20-unit buildings can have vastly different values based on these factors.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                <strong>Best Practice:</strong> Use price per unit as a preliminary screening tool or reality check, but always confirm value using the income approach and sales comparison approach for accurate valuation. Never rely solely on price per unit when making important financial decisions.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}